import { useSelector } from 'react-redux';
import { getEmptyStateByPmSensor, PULSE_EMPTY_STATES } from '../pulse.common';
import { myAccountSelector } from 'state/selectors/account';
import { ACCOUNT_TYPES_MAP, FEATURE_FLAGS_NAMES } from '@linearb/linjs-common';
import useRBACPermissions from '../../../hooks/useRBACPermissions';
import Icon from 'components/base/Icon';
import * as TrackingService from 'common/services/tracking.service';
import { TRACKING } from '@linearb/linui-utils';
import useTeams from '../../settings/views/ContributorsAndTeams/Teams/useTeams';
import { useMemo } from 'react';
import { useHistory } from 'react-router';
import sunglassesDudesWithDoorImg from 'images/characters/sunglasses-dudes-with-door.svg';
import sunglassesDudesImg from 'images/characters/sunglasses-dudes.svg';
import jiraLogo from 'images/logos/jira-logo.svg';
import shortcutLogo from 'images/logos/shortcut-logo.svg';
import azureBoardsLogo from 'images/logos/azure-boards.svg';
import notConnectedImg from 'images/pulse-not-connected.svg';
import saltImg from 'images/salt.svg';
import { StyledSvgIcon } from '../withPulseData';
import AppRouter from 'common/AppRouter';
import { isTeamLicenseSelector } from 'state/selectors/license';
import { pmIntegrationSelector } from 'state/selectors/sensors-authorizations';
import useFF from 'common/hooks/settings/useFF';
import teamSpaceUnconfiguredTeam from './empty-states/unconfigured-team';

const usePulseEmptyStateConfig = emptyStateType => {
  const account = useSelector(myAccountSelector);
  const canEdit = account.type_id !== ACCOUNT_TYPES_MAP.Viewer;
  const isTeamLicense = useSelector(isTeamLicenseSelector);
  const { isPermitted } = useRBACPermissions();
  const { onConfigureTeam } = useTeams();
  const history = useHistory();
  const pmSensor = useSelector(pmIntegrationSelector);
  const isTeamSpaceEnabled = useFF(FEATURE_FLAGS_NAMES.isTeamSpaceEnabled);

  const pulseText = isTeamSpaceEnabled ? 'Iterations' : 'Pulse';

  const onCreateTeamClick = () => {
    TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
      page: TRACKING.PAGES.PULSE,
      click_type: TRACKING.CLICK.ADD_TEAM
    });
    onConfigureTeam({ isSelectTeamId: true });
  };
  const onConnectPMClick = (click_type, provider) => {
    TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
      page: TRACKING.PAGES.PULSE,
      click_type
    });
    history.push({
      pathname: `${AppRouter.PROJECT_MANAGEMENT.path}/${provider}`,
      ...(!isTeamLicense ? { search: 'isOrgSettings=true' } : {})
    });
  };

  const emptyStateConfig = useMemo(() => {
    switch (emptyStateType) {
      case PULSE_EMPTY_STATES.NO_TEAM_SELECTED:
        return {
          notConnectedImg,
          title: isTeamSpaceEnabled
            ? 'Iterations is an individual team view'
            : 'Pulse is a team view',
          info:
            canEdit && isPermitted
              ? 'Select a team from the list above, or'
              : 'Select a team from the list above',
          image: sunglassesDudesImg,
          buttons:
            canEdit && isPermitted
              ? [
                  {
                    text: 'Create team',
                    icon: <Icon name='TeamsPeopleIcon' color='nepal' />,
                    onClick: onCreateTeamClick
                  }
                ]
              : [],
          afterButtonInfo:
            canEdit &&
            'After setting up, you will need to connect your team to a project management tool',
          onClick: onCreateTeamClick,
          buttonIcon: canEdit && <Icon name='TeamsPeopleIcon' color='nepal' />
        };
      case PULSE_EMPTY_STATES.NO_PM_CONNECTED:
        return {
          notConnectedImg,
          title: 'Just one little thing...',
          info: canEdit
            ? `To see your Team’s ${pulseText} view, connect your project management tool to LinearB:`
            : `To see your Team’s ${pulseText} view, ask your team lead or a LinearB admin to connect a project management tool to LinearB`,
          image: saltImg,
          buttons: canEdit
            ? [
                {
                  text: 'Jira',
                  icon: <StyledSvgIcon src={jiraLogo} alt='' />,
                  onClick: () =>
                    onConnectPMClick(TRACKING.CLICK.CONNECT_JIRA, 'jira')
                },
                {
                  text: 'Shortcut',
                  icon: <StyledSvgIcon src={shortcutLogo} alt='' />,
                  onClick: () =>
                    onConnectPMClick(
                      TRACKING.CLICK.CONNECT_CLUBHOUSE,
                      'clubhouse'
                    )
                },
                {
                  text: 'Azure Boards',
                  icon: <StyledSvgIcon src={azureBoardsLogo} alt='' />,
                  onClick: () =>
                    onConnectPMClick(
                      TRACKING.CLICK.CONNECT_AZURE_BOARDS,
                      'azure_boards'
                    )
                }
              ]
            : []
        };
      case PULSE_EMPTY_STATES.UNCONFIGURED_TEAM:
        const pmSensorConfig = getEmptyStateByPmSensor(pmSensor.sensor_id);
        return {
          notConnectedImg: pmSensorConfig.pulseImage,
          title: `Your team wants ${pulseText}`,
          info: canEdit
            ? `Select your team ${pmSensorConfig.name} so we can show you the right stories`
            : 'Ask your team lead or a LinearB admin to select a Jira board so we can show you the right stories',
          image: sunglassesDudesWithDoorImg,
          buttons: canEdit
            ? [
                {
                  text: pmSensorConfig.buttonText,
                  icon: <StyledSvgIcon src={pmSensorConfig.logo} alt='' />,
                  onClick: () => {
                    TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
                      page: TRACKING.PAGES.PULSE,
                      click_type: TRACKING.CLICK[pmSensorConfig.clickType]
                    });
                    history.push(
                      `${AppRouter.PROJECT_MANAGEMENT.path}/${pmSensorConfig.pathParam}`
                    );
                  }
                }
              ]
            : [],
          afterButtonInfo:
            canEdit &&
            `Note: It might take couple of minutes to see your data after selecting the ${pmSensorConfig.name}.`,
          ...(isTeamSpaceEnabled && teamSpaceUnconfiguredTeam)
        };
      case PULSE_EMPTY_STATES.TOO_LONG_ITERATION:
        return {
          notConnectedImg,
          title: 'Iterations cannot be longer than 90 days',
          info: 'Please select a different iteration to continue',
          image: sunglassesDudesImg
        };
      case PULSE_EMPTY_STATES.LOADING_PAST_ITERATIONS:
        return {
          notConnectedImg,
          title: 'No iterations available... For now.',
          info: 'Future iterations will appear here once things get rolling.',
          image: sunglassesDudesImg
        };

      default:
        return {};
    }
  }, [canEdit, isPermitted, emptyStateType]);

  return emptyStateConfig;
};

export default usePulseEmptyStateConfig;
